/**
 * CaseSelect Component
 * A searchable dropdown for selecting cases/dosare
 * Format: numar_intern | numar_dosar | client_name
 */

import { useState, useMemo, useCallback } from "react";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

// Remove diacritics for search comparison
const removeDiacritics = (str) => {
  if (!str) return "";
  return str
    .toLowerCase()
    .replace(/[ăâ]/g, 'a')
    .replace(/[î]/g, 'i')
    .replace(/[ș]/g, 's')
    .replace(/[ț]/g, 't')
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

// Extract numeric part from numar_dosar_intern for sorting
// Format: "142/XXXII" -> 142, "22/VII" -> 22
const extractNumericPart = (numărIntern) => {
  if (!numărIntern) return Infinity;
  const match = numărIntern.match(/^(\d+)/);
  return match ? parseInt(match[1], 10) : Infinity;
};

// Get client display name from case
const getClientName = (caseItem, clients = []) => {
  // First check if case has clients array populated
  if (caseItem.clients && caseItem.clients.length > 0) {
    const client = caseItem.clients[0];
    return client.display_name || client.nume || client.denumire || 
           `${client.prenume || ""} ${client.nume_familie || ""}`.trim() || "";
  }
  
  // Fallback: try to find client in clients list
  if (caseItem.client_ids && caseItem.client_ids.length > 0 && clients.length > 0) {
    const client = clients.find(c => c.id === caseItem.client_ids[0]);
    if (client) {
      return client.nume || client.denumire || 
             `${client.prenume || ""} ${client.nume_familie || ""}`.trim() || "";
    }
  }
  
  return "";
};

// Format case display: numar_intern | numar_dosar | client
const formatCaseDisplay = (caseItem, clients = []) => {
  const parts = [];
  
  // Add numar_dosar_intern (always first if exists)
  if (caseItem.numar_dosar_intern) {
    parts.push(caseItem.numar_dosar_intern);
  }
  
  // Add numar_dosar
  if (caseItem.numar_dosar && caseItem.numar_dosar !== "-") {
    parts.push(caseItem.numar_dosar);
  }
  
  // Add client name (always last)
  const clientName = getClientName(caseItem, clients);
  if (clientName) {
    parts.push(clientName);
  }
  
  return parts.length > 0 ? parts.join(" | ") : "Dosar fără identificare";
};

export function CaseSelect({
  cases = [],
  casesBC = [],
  clients = [],
  value,
  onValueChange,
  placeholder = "Selectează dosar...",
  emptyText = "Niciunul",
  allowNone = true,
  disabled = false,
  className,
  "data-testid": testId = "case-select",
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Sort cases: by numar_intern numeric, then "-" and "arhivat" at end alphabetically by client
  const sortedCases = useMemo(() => {
    // Don't filter out any cases - just sort them appropriately
    return [...cases].sort((a, b) => {
      const nrInternA = (a.numar_dosar_intern || "").toLowerCase();
      const nrInternB = (b.numar_dosar_intern || "").toLowerCase();
      
      // Check if it's a special case (-, arhivat, or starts with -)
      const isSpecialA = !nrInternA || nrInternA === "-" || nrInternA.startsWith("-") || nrInternA.includes("arhivat");
      const isSpecialB = !nrInternB || nrInternB === "-" || nrInternB.startsWith("-") || nrInternB.includes("arhivat");
      
      // Regular numbered cases come first
      if (!isSpecialA && isSpecialB) return -1;
      if (isSpecialA && !isSpecialB) return 1;
      
      // Both are special cases - sort alphabetically by client name
      if (isSpecialA && isSpecialB) {
        const clientNameA = getClientName(a, clients);
        const clientNameB = getClientName(b, clients);
        return removeDiacritics(clientNameA).localeCompare(removeDiacritics(clientNameB));
      }
      
      // Both have numbers - sort numerically by the number before /
      const numA = extractNumericPart(a.numar_dosar_intern);
      const numB = extractNumericPart(b.numar_dosar_intern);
      
      if (numA !== numB) return numA - numB;
      
      // If numbers are equal, fallback to string comparison
      return nrInternA.localeCompare(nrInternB);
    });
  }, [cases, clients]);

  // Sort BC cases the same way
  const sortedCasesBC = useMemo(() => {
    return [...casesBC].sort((a, b) => {
      const nrInternA = (a.numar_dosar_intern || "").toLowerCase();
      const nrInternB = (b.numar_dosar_intern || "").toLowerCase();
      const isSpecialA = !nrInternA || nrInternA === "-" || nrInternA.startsWith("-") || nrInternA.includes("arhivat");
      const isSpecialB = !nrInternB || nrInternB === "-" || nrInternB.startsWith("-") || nrInternB.includes("arhivat");
      if (!isSpecialA && isSpecialB) return -1;
      if (isSpecialA && !isSpecialB) return 1;
      if (isSpecialA && isSpecialB) {
        const clientNameA = getClientName(a, clients);
        const clientNameB = getClientName(b, clients);
        return removeDiacritics(clientNameA).localeCompare(removeDiacritics(clientNameB));
      }
      const numA = extractNumericPart(a.numar_dosar_intern);
      const numB = extractNumericPart(b.numar_dosar_intern);
      if (numA !== numB) return numA - numB;
      return nrInternA.localeCompare(nrInternB);
    });
  }, [casesBC, clients]);

  // All cases combined for value lookup
  const allCases = useMemo(() => [...cases, ...casesBC], [cases, casesBC]);

  // Filter cases by search query
  const filteredCases = useMemo(() => {
    if (!searchQuery.trim()) return sortedCases;
    
    const searchNormalized = removeDiacritics(searchQuery.trim());
    
    return sortedCases.filter(c => {
      // Search in client name
      const clientName = getClientName(c, clients);
      if (removeDiacritics(clientName).includes(searchNormalized)) return true;
      
      // Search in numar_dosar_intern
      const nrIntern = removeDiacritics(c.numar_dosar_intern || "");
      if (nrIntern.includes(searchNormalized)) return true;
      
      // Search in numar_dosar
      const nrDosar = removeDiacritics(c.numar_dosar || "");
      if (nrDosar.includes(searchNormalized)) return true;
      
      return false;
    });
  }, [sortedCases, searchQuery, clients]);

  // Filter BC cases by search query
  const filteredCasesBC = useMemo(() => {
    if (!searchQuery.trim()) return sortedCasesBC;
    
    const searchNormalized = removeDiacritics(searchQuery.trim());
    
    return sortedCasesBC.filter(c => {
      const clientName = getClientName(c, clients);
      if (removeDiacritics(clientName).includes(searchNormalized)) return true;
      const nrIntern = removeDiacritics(c.numar_dosar_intern || "");
      if (nrIntern.includes(searchNormalized)) return true;
      const nrDosar = removeDiacritics(c.numar_dosar || "");
      if (nrDosar.includes(searchNormalized)) return true;
      return false;
    });
  }, [sortedCasesBC, searchQuery, clients]);

  // Get display text for selected value
  const selectedCase = useMemo(() => {
    if (!value || value === "none") return null;
    return allCases.find(c => c.id === value);
  }, [allCases, value]);

  const selectedDisplayText = useMemo(() => {
    if (!selectedCase) return emptyText;
    return formatCaseDisplay(selectedCase, clients);
  }, [selectedCase, clients, emptyText]);

  const handleSelect = useCallback((caseId) => {
    onValueChange(caseId === "none" ? "" : caseId);
    setOpen(false);
    setSearchQuery("");
  }, [onValueChange]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", className)}
          data-testid={testId}
        >
          <span className="truncate text-left flex-1">
            {selectedCase ? (
              <span className="flex items-center gap-1 flex-wrap">
                {selectedCase.numar_dosar_intern && (
                  <span className="text-blue-600 font-medium">{selectedCase.numar_dosar_intern}</span>
                )}
                {selectedCase.numar_dosar_intern && (selectedCase.numar_dosar && selectedCase.numar_dosar !== "-") && (
                  <span className="text-muted-foreground">|</span>
                )}
                {selectedCase.numar_dosar && selectedCase.numar_dosar !== "-" && (
                  <span className="text-orange-600">{selectedCase.numar_dosar}</span>
                )}
                {(selectedCase.numar_dosar_intern || (selectedCase.numar_dosar && selectedCase.numar_dosar !== "-")) && 
                 getClientName(selectedCase, clients) && (
                  <span className="text-muted-foreground">|</span>
                )}
                {getClientName(selectedCase, clients) && (
                  <span className="text-green-600">{getClientName(selectedCase, clients)}</span>
                )}
              </span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start" data-case-select-popover>
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              placeholder="Caută după nr. intern, nr. dosar sau client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              data-testid={`${testId}-search`}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="ml-2 rounded-sm opacity-70 hover:opacity-100"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <CommandList>
            <CommandEmpty>
              {searchQuery ? `Niciun dosar găsit pentru "${searchQuery}"` : "Nu există dosare"}
            </CommandEmpty>
            <CommandGroup heading="Dosare">
              {allowNone && (
                <CommandItem
                  value="none"
                  onSelect={() => handleSelect("none")}
                  className="cursor-pointer [&[data-selected=true]]:bg-slate-100 [&[data-selected=true]]:text-slate-900"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      (!value || value === "none") ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="text-muted-foreground">{emptyText}</span>
                </CommandItem>
              )}
              {filteredCases.map((caseItem) => {
                const clientName = getClientName(caseItem, clients);
                return (
                  <CommandItem
                    key={caseItem.id}
                    value={caseItem.id}
                    onSelect={() => handleSelect(caseItem.id)}
                    className="cursor-pointer [&[data-selected=true]]:bg-slate-100 [&[data-selected=true]]:text-slate-900"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        value === caseItem.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="flex items-center gap-1 flex-wrap overflow-hidden">
                      {caseItem.numar_dosar_intern && (
                        <span className="text-blue-600 font-medium">{caseItem.numar_dosar_intern}</span>
                      )}
                      {caseItem.numar_dosar_intern && (caseItem.numar_dosar && caseItem.numar_dosar !== "-") && (
                        <span className="text-muted-foreground">|</span>
                      )}
                      {caseItem.numar_dosar && caseItem.numar_dosar !== "-" && (
                        <span className="text-orange-600">{caseItem.numar_dosar}</span>
                      )}
                      {(caseItem.numar_dosar_intern || (caseItem.numar_dosar && caseItem.numar_dosar !== "-")) && clientName && (
                        <span className="text-muted-foreground">|</span>
                      )}
                      {clientName && (
                        <span className="text-green-600 truncate">{clientName}</span>
                      )}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {filteredCasesBC.length > 0 && (
              <CommandGroup heading="Dosare BC">
                {filteredCasesBC.map((caseItem) => {
                  const clientName = getClientName(caseItem, clients);
                  return (
                    <CommandItem
                      key={caseItem.id}
                      value={caseItem.id}
                      onSelect={() => handleSelect(caseItem.id)}
                      className="cursor-pointer [&[data-selected=true]]:bg-slate-100 [&[data-selected=true]]:text-slate-900"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 shrink-0",
                          value === caseItem.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="flex items-center gap-1 flex-wrap overflow-hidden">
                        <span className="text-purple-600 font-medium text-[10px] px-1 py-0 bg-purple-50 rounded border border-purple-200">BC</span>
                        {caseItem.numar_dosar_intern && (
                          <span className="text-blue-600 font-medium">{caseItem.numar_dosar_intern}</span>
                        )}
                        {caseItem.numar_dosar_intern && (caseItem.numar_dosar && caseItem.numar_dosar !== "-") && (
                          <span className="text-muted-foreground">|</span>
                        )}
                        {caseItem.numar_dosar && caseItem.numar_dosar !== "-" && (
                          <span className="text-orange-600">{caseItem.numar_dosar}</span>
                        )}
                        {(caseItem.numar_dosar_intern || (caseItem.numar_dosar && caseItem.numar_dosar !== "-")) && clientName && (
                          <span className="text-muted-foreground">|</span>
                        )}
                        {clientName && (
                          <span className="text-green-600 truncate">{clientName}</span>
                        )}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default CaseSelect;
